import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

// Import translation files
import deTranslations from "../locales/de/common.json";
import enTranslations from "../locales/en/common.json";
import esTranslations from "../locales/es/common.json";
import frTranslations from "../locales/fr/common.json";
import hiTranslations from "../locales/hi/common.json";
import idTranslations from "../locales/id/common.json";
import itTranslations from "../locales/it/common.json";
import jaTranslations from "../locales/ja/common.json";
import koTranslations from "../locales/ko/common.json";
import nlTranslations from "../locales/nl/common.json";
import plTranslations from "../locales/pl/common.json";
import ptTranslations from "../locales/pt/common.json";
import ruTranslations from "../locales/ru/common.json";
import trTranslations from "../locales/tr/common.json";
import viTranslations from "../locales/vi/common.json";
import zhTranslations from "../locales/zh/common.json";

// Define supported languages - expandable to 10 languages
export const SUPPORTED_LANGUAGES = {
  en: { code: "en", name: "English", nativeName: "English" },
  es: { code: "es", name: "Spanish", nativeName: "Español" },
  fr: { code: "fr", name: "French", nativeName: "Français" },
  de: { code: "de", name: "German", nativeName: "Deutsch" },
  it: { code: "it", name: "Italian", nativeName: "Italiano" },
  pt: { code: "pt", name: "Portuguese", nativeName: "Português" },
  ja: { code: "ja", name: "Japanese", nativeName: "日本語" },
  ko: { code: "ko", name: "Korean", nativeName: "한국어" },
  ru: { code: "ru", name: "Russian", nativeName: "Русский" },
  hi: { code: "hi", name: "Hindi", nativeName: "हिन्दी" },
  nl: { code: "nl", name: "Dutch", nativeName: "Nederlands" },
  pl: { code: "pl", name: "Polish", nativeName: "Polski" },
  zh: { code: "zh", name: "Simplified Chinese", nativeName: "简体中文" },
  vi: { code: "vi", name: "Vietnamese", nativeName: "Tiếng Việt" },
  tr: { code: "tr", name: "Turkish", nativeName: "Türkçe" },
  id: { code: "id", name: "Indonesian", nativeName: "Bahasa Indonesia" },
} as const;

export type SupportedLanguage = keyof typeof SUPPORTED_LANGUAGES;

// Resources object with current translations
const resources = {
  en: {
    common: enTranslations,
  },
  es: {
    common: esTranslations,
  },
  fr: {
    common: frTranslations,
  },
  de: {
    common: deTranslations,
  },
  it: {
    common: itTranslations,
  },
  pt: {
    common: ptTranslations,
  },
  ru: {
    common: ruTranslations,
  },
  ja: {
    common: jaTranslations,
  },
  hi: {
    common: hiTranslations,
  },
  pl: {
    common: plTranslations,
  },
  zh: {
    common: zhTranslations,
  },
  id: {
    common: idTranslations,
  },
  tr: {
    common: trTranslations,
  },
  nl: {
    common: nlTranslations,
  },
  vi: {
    common: viTranslations,
  },
  ko: {
    common: koTranslations,
  },
};

i18n
  // Detect user language
  .use(LanguageDetector)
  // Pass the i18n instance to react-i18next
  .use(initReactI18next)
  // Initialize i18next
  .init({
    // Default language
    fallbackLng: "en",

    // Available languages
    supportedLngs: Object.keys(SUPPORTED_LANGUAGES),

    // Language detection options
    detection: {
      // Detection order and options
      order: ["localStorage", "navigator", "htmlTag"],

      // Cache user language on
      caches: ["localStorage"],

      // Only detect languages that are in the supportedLngs list
      checkWhitelist: true,
    },

    // Resources (translations)
    resources,

    // Default namespace
    defaultNS: "common",

    // Interpolation options
    interpolation: {
      escapeValue: false, // React already does escaping
    },

    // React i18next options
    react: {
      // Bind events to components
      bindI18n: "languageChanged",
      // Auto-sync the i18n state with React
      useSuspense: false,
    },

    // Development options
    debug: process.env.NODE_ENV === "development",

    // Namespace separator
    nsSeparator: ":",

    // Key separator
    keySeparator: ".",
  });

export default i18n;
